import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { IRootState } from '../../../redux';
// Components
import { ConnectDropTarget, DropTargetMonitor, DropTargetConnector, DropTarget } from 'react-dnd';
// Assets
import images from '../../../assets/images/images';
import viewFolderStyles from './../viewfolders.module.scss';
// helpers
import { isMediaSizeMD, isMediaSizeMoreThanLG } from '../../../helpers/layout';
import { getUnreadCountByFolderId, getFolderName } from '../../../helpers/folder';
import { isTouchScreen } from '../../../helpers/browser';
import { clearWhitespaces, getMovingAllowedTypes } from '../../../helpers/general';
import KeyboardHandler from '../../../helpers/keyboardHandler';
// Interfaces
import { IFolder, IViewFolder, virtualFoldersEnum } from '../../../interfaces/folder.interfaces';
import { DragItemTypes, IMessage } from '../../../interfaces/message.interfaces';
// Enums
import { FolderTypeNames, MenuBarButtonType, MoveToFolderItems } from '../../../enums';
// Config
import { ConfigContext } from '../../../providers';
import { Image } from '../..';
import {
    DeleteIcon,
    FolderIcon,
    InboxIcon,
    PaperPlaneIcon,
    PencilIcon,
    LetterIcon,
    FlagIcon,
} from '../../../assets/images/svg';

interface IFolderItemProps {
    maxFolderLevels: number;
    folder: IViewFolder;
    level: number;
    itemIndex: number;
    onActiveFolderClick: (folder: IFolder) => void;
    onToggleParentClick: (folder: IViewFolder, e: React.MouseEvent) => void;
    onDragDeleteMessages: (action: MenuBarButtonType, viewedDocument: IMessage) => void;
    onDragMoveMessages: (action: MoveToFolderItems, folderId: string) => void;
    canDrop: boolean;
    isOver: boolean;
    connectDropTarget: ConnectDropTarget;
    isMailBox?: boolean;
}

class FolderItem extends React.Component<ReduxType> {
    private ident = `${clearWhitespaces(this.props.folder.name) + this.props.itemIndex}_${this.props.level}`;
    folderItemRef = React.createRef<HTMLDivElement>();
    keyboardHandlerInstance = new KeyboardHandler();

    componentDidMount() {
        this.setupKeyboardHandler();
    }

    setupKeyboardHandler = () => {
        this.keyboardHandlerInstance.addHandlers({
            enter: this.enterKeyHandler,
            up: this.upKeyHandler,
            down: this.downKeyHandler,
            right: this.toggleFolderChildren,
            left: this.toggleFolderChildren,
        });
    };

    enterKeyHandler = () => {
        this.props.onActiveFolderClick(this.props.folder);
    };

    upKeyHandler = (keyEvent: React.KeyboardEvent) => {
        const prevElement = keyEvent.currentTarget.previousSibling as HTMLElement;
        const prevFolder = (
            prevElement && this.checkIsFolderSeparator(prevElement) ? prevElement.previousSibling : prevElement
        ) as HTMLElement;
        if (prevFolder) prevFolder.focus();
    };

    downKeyHandler = (keyEvent: React.KeyboardEvent) => {
        const nextElement = keyEvent.currentTarget.nextSibling as HTMLElement;
        const nextFolder = (
            nextElement && this.checkIsFolderSeparator(nextElement) ? nextElement.nextSibling : nextElement
        ) as HTMLElement;
        if (nextElement) nextFolder.focus();
    };

    toggleFolderChildren = () => {
        this.props.onToggleParentClick(this.props.folder, null);
    };

    checkIsFolderSeparator = (folderEl: HTMLElement) => {
        return folderEl.classList.contains(viewFolderStyles.separator);
    };

    onMouseLeave = (event: React.MouseEvent) => {
        const activeElement = event.currentTarget as HTMLElement;
        activeElement.blur();
    };

    onMouseEnter = (event: React.MouseEvent) => {
        const activeElement = event.currentTarget as HTMLElement;
        activeElement.focus();
    };

    getAriaLabelledBy = (config: any) => {
        return this.props.folder.children.length > 0 &&
            this.props.folder.level <= this.props.maxFolderLevels &&
            config.features.folder.customFolders
            ? `expander${this.ident} img${this.ident} description${this.ident}`
            : `img${this.ident} description${this.ident}`;
    };

    public render() {
        const unreadCount = getUnreadCountByFolderId(this.props.folder.id, this.props.unreadCounter, null);
        const isActiveFolder =
            this.props.selectedFolder &&
            this.props.selectedFolder.id === this.props.folder.id &&
            !this.props.searchValue &&
            this.props.isMailBox;

        return (
            <ConfigContext.Consumer>
                {(config): JSX.Element =>
                    this.props.connectDropTarget(
                        <div
                            // tabIndex={this.props.level === 0 && this.props.itemIndex === 0 ? 0 : -1}
                            // role="link"
                            tabIndex={0}
                            role="button"
                            ref={this.folderItemRef}
                            className={`${
                                isActiveFolder && this.props.selectedSender == null ? viewFolderStyles.selected : ''
                            } ${this.props.isOver ? viewFolderStyles.isOver : ''} ${viewFolderStyles.folder_inner} ${
                                viewFolderStyles.navItem
                            } d-flex flex-row align-items-center col-lg-12 p-0`}
                            key={this.ident}
                            id={`folderItem${this.ident}`}
                            aria-labelledby={this.getAriaLabelledBy(config)}
                            {...(!isTouchScreen() && {
                                onKeyDown: this.keyboardHandlerInstance.onPress,
                                onMouseLeave: this.onMouseLeave,
                                onMouseEnter: this.onMouseEnter,
                            })}
                            onClick={() => this.props.onActiveFolderClick(this.props.folder)}
                        >
                            {isMediaSizeMoreThanLG(this.props.mediaSize) || this.props.sidebarShown ? (
                                <>
                                    <div
                                        id={`toggle${this.ident}`}
                                        className={[
                                            viewFolderStyles.toggleExpanderIconWrap,
                                            viewFolderStyles[`mlL${this.props.level}`],
                                            'd-flex align-items-center py-2',
                                        ].join(' ')}
                                        onClick={(event) => this.props.onToggleParentClick(this.props.folder, event)}
                                    >
                                        {this.props.folder.children.length > 0 &&
                                        this.props.folder.level <= this.props.maxFolderLevels &&
                                        config.features.folder.customFolders ? (
                                            <Image
                                                src={
                                                    this.props.folder.showChildren
                                                        ? images.expandDarkSmall
                                                        : images.collapseDarkSmall
                                                }
                                                id={`expander${this.ident}`}
                                                aria-label={this.props.intl.formatMessage({ id: 'EXPAND_AREA' })}
                                                alt="EXPAND"
                                                tabIndex={0}
                                                role="button"
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        this.props.onToggleParentClick(this.props.folder, null);
                                                    }
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                    <div
                                        className={generateLevelMinWidth(
                                            [
                                                viewFolderStyles.folderLine,
                                                'd-flex justify-content-between align-items-center flex-row',
                                            ].join(' '),
                                            this.props.level
                                        )}
                                    >
                                        <div
                                            className={[
                                                'd-flex align-items-center w-100',
                                                isMediaSizeMD(this.props.mediaSize) && !this.props.sidebarShown
                                                    ? 'justify-content-center w-100'
                                                    : '',
                                            ].join(' ')}
                                        >
                                            <div
                                                className={[
                                                    'd-flex align-items-start py-2',
                                                    isMediaSizeMoreThanLG(this.props.mediaSize) ||
                                                    this.props.sidebarShown
                                                        ? 'mr-2'
                                                        : '',
                                                ].join(' ')}
                                            >
                                                {getIcon(this.props.folder.folderType)}
                                            </div>
                                            {isMediaSizeMoreThanLG(this.props.mediaSize) || this.props.sidebarShown ? (
                                                <div
                                                    id={`description${this.ident}`}
                                                    className={generateLevelWidth(
                                                        [
                                                            viewFolderStyles.folderText,
                                                            'align-items-start py-2 px-0',
                                                        ].join(' '),
                                                        this.props.level,
                                                        unreadCount
                                                    )}
                                                    style={{ color: config.colors.sidebar.folderNameColor }}
                                                >
                                                    {getFolderName(this.props.folder, this.props.intl.formatMessage)}
                                                </div>
                                            ) : null}
                                        </div>
                                        {unreadCount > 0 ? (
                                            <div className={[viewFolderStyles.unreadCount].join(' ')}>
                                                {`(${unreadCount})`}
                                            </div>
                                        ) : null}
                                        {this.props.folder.folderType === FolderTypeNames.UNREAD &&
                                        this.props.unreadCounter?.unreadCount > 0 ? (
                                            <div className={[viewFolderStyles.unreadCount].join(' ')}>
                                                {`(${this.props.unreadCounter?.unreadCount})`}
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <div className="d-flex align-items-center justify-content-center w-100 py-2">
                                    {getIcon(this.props.folder.folderType)}
                                </div>
                            )}
                        </div>
                    )
                }
            </ConfigContext.Consumer>
        );
    }
}

const generateLevelMinWidth = (className = '', level: number): string => {
    const classNames = [];
    classNames.push(viewFolderStyles['mwL' + level]);

    if (className) classNames.push(className);

    return classNames.join(' ');
};

const generateLevelWidth = (className = '', level: number, unreadCount: number): string => {
    const classNames = [];

    classNames.push(viewFolderStyles['wL' + level]);

    if (className) classNames.push(className);

    return classNames.join(' ');
};

const getIcon = (folderType: string): JSX.Element => {
    switch (folderType) {
        case FolderTypeNames.DRAFT:
            return <PencilIcon />;

        case FolderTypeNames.INBOX:
            return <InboxIcon />;

        case FolderTypeNames.SENT:
            return <PaperPlaneIcon />;

        case FolderTypeNames.UNREAD:
            return <LetterIcon />;

        case FolderTypeNames.FLAGGEDFOLDER:
            // return <Image src={images.flagFolder} alt="FLAG" style={{ height: '24px' }} />;
            return <FlagIcon />;

        case FolderTypeNames.TRASH:
            return <DeleteIcon />;

        default:
            return <FolderIcon />;
    }
};

const mapStateToProps = ({ mailbox, sidebar, appReadouts, sender }: IRootState) => {
    return {
        selectedFolder: mailbox.selectedFolder,
        sidebarShown: sidebar.sidebarShown,
        unreadCounter: mailbox.unreadCounter,
        mediaSize: appReadouts.mediaSize,
        searchValue: mailbox.searchValue,
        selectedSender: mailbox.selectedSender,
    };
};

type ReduxType = IFolderItemProps & WrappedComponentProps & ReturnType<typeof mapStateToProps>;

const reduxConnected = connect(mapStateToProps)(injectIntl(FolderItem));

export default DropTarget(
    DragItemTypes.MAILITEM,
    {
        canDrop(props: IFolderItemProps, monitor: DropTargetMonitor) {
            const dragObject = monitor.getItem();
            return (
                dragObject &&
                props.folder.id !== virtualFoldersEnum.MORE_FOLDERS &&
                getMovingAllowedTypes(dragObject.item.msgState).includes(props.folder.folderType) &&
                (props.folder.id !== dragObject.item.sourceFolderId || dragObject.item.trashed) &&
                dragObject.item.allowDragAndDrop
            );
        },
        drop(props: IFolderItemProps, monitor: DropTargetMonitor, component: any) {
            if (props.folder.folderType === FolderTypeNames.TRASH) {
                if (props.onDragDeleteMessages) {
                    props.onDragDeleteMessages(MenuBarButtonType.MAIL_DELETE, null);
                }
            } else {
                if (props.onDragMoveMessages) {
                    props.onDragMoveMessages(MoveToFolderItems.CUSTOM_FOLDERS, props.folder.id);
                }
            }
        },
        hover(props: IFolderItemProps, monitor: DropTargetMonitor, component: any) {
            const dragObject = monitor.getItem();

            dragObject.item.overBlocked =
                !monitor.canDrop() ||
                !getMovingAllowedTypes(dragObject.item.msgState).includes(props.folder.folderType);

            if (props.folder.children.length > 0 && !props.folder.showChildren) {
                props.onToggleParentClick(props.folder, null);
            }
        },
    },
    (connect: DropTargetConnector, monitor: DropTargetMonitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
    })
)(reduxConnected);
